import { Utils } from "./utils";
import type { Payload } from "./config";
import type { ClientSummary, ClientAggregate, LogEntry, OrgConfig, HmrcResponse, ApiEnvelope } from "../../shared/contract";

// Injected at build time by esbuild `define` (see build.mjs); falls back to prod.
declare const __API_ENDPOINT__: string;

const settings = {
    endpoint: __API_ENDPOINT__
};

/**
 * Typed client for the backend API — the single boundary between the web app
 * and the Lambda. Every command POSTs to one endpoint with a Cognito id token.
 */
export class Api {
    private token: string;
    private refreshFn?: () => Promise<string>;
    private onExpired?: () => void;
    private refreshInFlight: Promise<boolean> | null = null;

    public setToken(token: string) {
        this.token = token;
    }

    public getToken(): string {
        return this.token;
    }

    /**
     * Wire up session renewal: `refresh` mints a fresh token (and we adopt it),
     * `onExpired` is called when renewal is impossible so the app can bounce the
     * user to login. Set once at startup — see index.ts.
     */
    public setAuthHandlers(handlers: { refresh: () => Promise<string>; onExpired: () => void }) {
        this.refreshFn = handlers.refresh;
        this.onExpired = handlers.onExpired;
    }

    /** True if the current id token expires within `skewSeconds` (or is already expired/unreadable-as-valid). */
    private tokenExpiringSoon(skewSeconds = 90): boolean {
        try {
            const [, payload] = this.token.split('.');
            const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            if (!claims.exp) return false;
            return claims.exp * 1000 - Date.now() < skewSeconds * 1000;
        } catch {
            return false;
        }
    }

    /**
     * Single-flight token refresh: concurrent callers share one in-flight
     * refresh rather than each firing their own. Resolves true on success.
     */
    private refresh(): Promise<boolean> {
        if (!this.refreshFn) return Promise.resolve(false);
        if (this.refreshInFlight) return this.refreshInFlight;
        this.refreshInFlight = (async () => {
            try {
                this.setToken(await this.refreshFn!());
                return true;
            } catch {
                return false;
            }
        })();
        this.refreshInFlight.finally(() => { this.refreshInFlight = null; });
        return this.refreshInFlight;
    }

    /** Swap an HMRC authorization code for an access token (stored server-side). */
    public async authorize(code: string): Promise<void> {
        await this.call("authorize", { code });
    }

    public async getClients(): Promise<ClientSummary[]> {
        const res = await this.call("get-clients", {});
        return res.status?.success ? (res.data as ClientSummary[]) : [];
    }

    public async getClient(investorId: string): Promise<ClientAggregate> {
        const res = await this.call("get-client", { investorId });
        if (!res.status?.success)
            throw new Error("Investor not found");
        return res.data as ClientAggregate;
    }

    public async getLog(): Promise<LogEntry[]> {
        const res = await this.call("get-log", {});
        if (!res.status?.success)
            throw new Error("No logs");
        return res.data as LogEntry[];
    }

    public async getOrgConfig(): Promise<OrgConfig> {
        const res = await this.call("get-org", {});
        if (!res.status?.success)
            throw new Error("Failed to load org config");
        const cfg = res.data as OrgConfig;
        if (!cfg?.env || !cfg?.hmrcClientId)
            throw new Error("Incomplete org config — env or hmrcClientId missing");
        return cfg;
    }

    /**
     * Execute a LISA command. The result shape varies by command/outcome: a raw
     * HMRC response (status + data) for reads/errors, or a refreshed client
     * aggregate after a successful write. Callers narrow by checking `.status`.
     */
    public async lisa(command: string, payload: Payload): Promise<HmrcResponse | ClientAggregate> {
        const res = await this.call(command, payload);
        if (!res.status?.success)
            throw new Error("LISA command failed: " + command);
        return res.data;
    }

    private doFetch(command: string, data?: Record<string, any>): Promise<Response> {
        return fetch(settings.endpoint, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`,
            },
            body: JSON.stringify({ command, data }),
        });
    }

    private async call(command: string, data?: Record<string, any>): Promise<ApiEnvelope<any>> {
        try {
            Utils.showLoader();

            // Proactive (just-in-time) renewal: swap an about-to-expire id token before sending.
            if (this.tokenExpiringSoon())
                await this.refresh();

            let response = await this.doFetch(command, data);

            // Reactive fallback: the token died anyway — refresh once and retry the request.
            if (response.status === 401 && await this.refresh())
                response = await this.doFetch(command, data);

            // Still unauthorized => renewal is impossible (refresh token expired/revoked).
            if (response.status === 401) {
                Utils.hideLoader();
                this.onExpired?.();
                return { status: { success: false }, data: null, error: "Session expired" };
            }

            Utils.hideLoader();
            const json = await response.json() as ApiEnvelope<any>;
            if (response.ok)
                return json;
            console.error(`API error: ${command} → ${response.status}`);
            throw new Error(`HTTP error: ${response.status}`);
        } catch (error) {
            Utils.hideLoader();
            console.error(`API request failed: ${command}`);
            return { status: { success: false }, data: null, error: String(error) };
        }
    }
}
